import isEqual from 'lodash/isEqual';
import {MutableRefObject, useCallback, useRef} from 'react';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import {events} from '../constant/textViewerSagaEvents';
import {FrameElements} from '../type/FrameElements.interface';
import {HtmlInfo} from '../type/HtmlInfo.interface';
import {makeFrameElements} from '../type/makeFrameElements';
import {TextViewerCoreOption} from '../type/TextViewerCoreOption.type';
import {TextViewerReadType} from '../type/TextViewerReadType';
import {applyContentElementVisibility} from '../util/applyContentElementVisibility';
import {applyHtmlInfo} from '../util/applyHtmlInfo';
import {applyStyles} from '../util/applyStyles';
import {initializeFrameElements} from '../util/initializeFrameElements';
import {makePartialFrameElements} from '../util/makePartialHostElement';

export function useDrawHtmlInfoHandler({
  productId,
  readType,
  containerElement,
  wrapperElement,
  htmlInfo,
  htmlInfoList,
  prevHtmlInfo,
  option,
  currentChapterId,
  onClickImage,
}:
  | {
      productId: number;
      readType: TextViewerReadType;
      containerElement?: HTMLDivElement | null;
      wrapperElement?: HTMLDivElement | null;
      htmlInfo: HtmlInfo;
      prevHtmlInfo?: HtmlInfo;
      htmlInfoList?: never;
      option: TextViewerCoreOption;
      onClickImage?: (element: HTMLImageElement | SVGSVGElement) => void;
      currentChapterId?: never;
    }
  | {
      productId: number;
      readType?: never;
      containerElement?: HTMLDivElement | null;
      wrapperElement?: never;
      htmlInfo?: never;
      prevHtmlInfo?: never;
      htmlInfoList: HtmlInfo[];
      option: TextViewerCoreOption;
      onClickImage?: never;
      currentChapterId?: number;
    }): {
  frameElementsRef: MutableRefObject<FrameElements | null>;
  redrawHtmlInfo: () => Promise<void>;
  redrawHtmlInfoList: () => Promise<void>;
} {
  const frameElementsRef = useRef<FrameElements | null>(null);
  const dispatch = useTypedDispatch();

  const redrawHtmlInfo = useCallback(async () => {
    if (!containerElement) {
      return;
    }

    const frameElements = frameElementsRef.current;
    dispatch(events.redrawContentRequired({productId}));

    // 기존에 있던 컨텐츠를 제거합니다.
    if (frameElements) {
      frameElements.shadowHostElement.parentElement?.removeChild(
        frameElements.shadowHostElement
      );
    }

    const newframeElements = makeFrameElements();
    initializeFrameElements(newframeElements);
    containerElement.appendChild(newframeElements.documentFragment);
    frameElementsRef.current = newframeElements;

    applyContentElementVisibility({
      frameElements: newframeElements,
      visible: false,
    });
    applyHtmlInfo({
      frameElements: newframeElements,
      htmlInfo: htmlInfo as HtmlInfo,
    });
    await applyStyles({
      frameElements: newframeElements,
      onClickImage,
      option,
      isPageTextViewer: readType === TextViewerReadType.Page,
    });

    if (readType === TextViewerReadType.Page) {
      dispatch(
        events.redrawContentCompleted({
          productId,
          readType: TextViewerReadType.Page,
        })
      );
    } else {
      const wrapperStartY = wrapperElement?.offsetTop || 0;
      const wrapperBottomY =
        (wrapperElement?.offsetTop || 0) + (wrapperElement?.offsetHeight || 0);

      dispatch(
        events.redrawContentCompleted({
          productId,
          readType: TextViewerReadType.Scroll,
          size: option.size,
          wrapperStartY,
          wrapperBottomY,
          needScrollAdjust: !isEqual(prevHtmlInfo, htmlInfo),
        })
      );
    }
  }, [
    containerElement,
    dispatch,
    htmlInfo,
    onClickImage,
    option,
    prevHtmlInfo,
    productId,
    readType,
    wrapperElement,
  ]);

  const redrawHtmlInfoList = useCallback(async () => {
    if (!containerElement) {
      return;
    }

    dispatch(events.redrawContentRequired({productId}));

    // 기존에 있던 컨텐츠를 제거합니다.
    while (containerElement.firstChild) {
      containerElement.removeChild(containerElement.firstChild);
    }

    const documentFragment = new DocumentFragment();
    const newPartialFrameElementList = (htmlInfoList as HtmlInfo[]).map(() => {
      const newPartialframeElements = makePartialFrameElements();
      initializeFrameElements(newPartialframeElements);
      documentFragment.appendChild(newPartialframeElements.shadowHostElement);
      return newPartialframeElements;
    });

    containerElement.appendChild(documentFragment);

    const processFrameElementsPromises = newPartialFrameElementList.map(
      async (newframeElements, index) => {
        applyContentElementVisibility({
          frameElements: newframeElements,
          visible: false,
        });
        applyHtmlInfo({
          frameElements: newframeElements,
          htmlInfo: (htmlInfoList as HtmlInfo[])[index],
        });
        await applyStyles({
          frameElements: newframeElements,
          option,
          isPageTextViewer: false,
        });
      }
    );

    await Promise.all(processFrameElementsPromises);

    // 기존 챕터를 대상으로 스크롤 위치를 복구합니다.
    const containerRect = (
      containerElement.childNodes[currentChapterId ?? 0] as HTMLDivElement
    ).getBoundingClientRect();

    const wrapperStartY = window.scrollY + containerRect.y;
    const wrapperBottomY = wrapperStartY + containerRect.height;

    dispatch(
      events.redrawContentCompleted({
        productId,
        readType: TextViewerReadType.Scroll,
        size: option.size,
        wrapperStartY,
        wrapperBottomY,
        needScrollAdjust: true,
        isPreview: !!htmlInfoList,
      })
    );
  }, [
    containerElement,
    currentChapterId,
    dispatch,
    htmlInfoList,
    option,
    productId,
  ]);

  return {
    frameElementsRef,
    redrawHtmlInfo,
    redrawHtmlInfoList,
  };
}
